/*
 * The bundle flow's product drawer: a plain custom element, not a subclass
 * of anything Dawn ships — this theme has no cart drawer to extend
 * (settings.cart_type is "notification", see the add-to-cart-no-popup
 * memory), so there is nothing reusable here beyond the pattern itself.
 *
 * <bundle-drawer> owns only open/close chrome (overlay click, Escape,
 * focus). Its body content is opaque HTML handed in by bundle-builder.js
 * via setBody() — this file never fetches anything itself.
 *
 * A second drawer (the bundle-cart drawer: contents/progress/"View my
 * cart") is Task 6's job and does not exist yet (ruling 6) — only one
 * <bundle-drawer> instance is wired up on this page today.
 */
(function () {
  if (window.__bundleDrawersInit) return;
  window.__bundleDrawersInit = true;

  /*
   * Elements a real Tab press can land on. Kept in one place so the focus
   * trap below and getFocusableElements() agree on what counts.
   */
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /*
   * Same-name radios (the drawer's Size/length fieldsets) form a single
   * native tab stop each — the browser only lets Tab land on the checked
   * radio in a group (or the first, if none is checked yet), the rest are
   * skipped over natively. Mirroring that here means the trap's computed
   * "first"/"last" match where a real Tab keypress actually stops, so the
   * boundary check below (active === first/last) fires reliably instead of
   * missing because the DOM's *first* radio in a group was never a real
   * tab stop to begin with.
   */
  function getFocusableElements(panel) {
    var all = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));
    var seenGroups = {};

    return all.filter(function (el) {
      /*
       * The panel holds both views at once and hides the inactive one, so a
       * plain selector sweep also finds the controls of the view nobody can
       * see. They are not real tab stops, and counting them would put the
       * trap's "first"/"last" on elements Tab never reaches — the boundary
       * check below would then never fire.
       */
      if (el.closest('[hidden]')) return false;

      if (el.tagName !== 'INPUT' || el.type !== 'radio') return true;

      var name = el.name;
      if (seenGroups[name]) return false;
      seenGroups[name] = true;

      var group = panel.querySelectorAll('input[type="radio"][name="' + CSS.escape(name) + '"]:not([disabled])');
      var checked = Array.from(group).find(function (radio) { return radio.checked; });
      return checked ? el === checked : el === group[0];
    });
  }

  class BundleDrawer extends HTMLElement {
    constructor() {
      super();
      this._returnFocusTo = null;
      this._sizeGuideReturnFocusTo = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-size-guide-open]')) {
          this.showSizeGuide(event.target.closest('[data-drawer-size-guide-open]'));
          return;
        }

        /* The size guide's own × goes back one step, not out: see
           snippets/bundle-drawer-shell.liquid. */
        if (event.target.closest('[data-drawer-size-guide-close]')) {
          this.hideSizeGuide();
          return;
        }

        if (event.target.closest('[data-size-guide-toggle]')) {
          this.toggleSizeGuideView(event.target.closest('[data-size-guide-toggle]'));
          return;
        }

        if (
          event.target.closest('[data-drawer-close]') ||
          event.target.hasAttribute('data-drawer-overlay')
        ) {
          this.close();
        }
      });

      /*
       * Focus trap (task-5 finding 1). Without this, Tab from the last
       * focusable control in the panel — or Shift+Tab from the first —
       * walks out into the rest of the document and eventually browser
       * chrome (the address bar has no DOM node, so nothing here can catch
       * it once focus reaches it). At that point a real Escape keypress
       * never reaches the document-level listener below, because it never
       * reaches the document at all. Cycling focus back into the panel at
       * both ends keeps Escape reachable no matter how far the shopper
       * tabs.
       */
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !this.hasAttribute('open')) return;

        var panel = this.querySelector('.bundle-drawer__panel');
        if (!panel) return;

        var focusable = getFocusableElements(panel);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        var active = document.activeElement;

        if (event.shiftKey) {
          if (active === first || !panel.contains(active)) {
            event.preventDefault();
            last.focus();
          }
        } else if (active === last || !panel.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      });

      /*
       * Bound once per instance (not per open()) so repeated opens don't
       * pile up duplicate document-level listeners — there is exactly one
       * <bundle-drawer> on the page, so this runs for the lifetime of the
       * page either way, but staying tidy here costs nothing.
       */
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.hasAttribute('open')) return;

        /* Escape unwinds one layer at a time, the same way the × does. */
        if (this.getAttribute('data-view') === 'size-guide') {
          this.hideSizeGuide();
          return;
        }

        this.close();
      });
    }

    open(opener) {
      /* Remembers what had focus before the drawer opened (typically the
         card or size button that triggered it) so close() can hand focus
         back there instead of dropping it to <body>. */
      this._returnFocusTo = opener instanceof HTMLElement ? opener : document.activeElement;

      this.showView('product');
      this.setAttribute('open', '');
      this.removeAttribute('aria-hidden');
      document.body.classList.add('bundle-drawer-open');

      const closeButton = this.querySelector('[data-drawer-close]');
      if (closeButton) closeButton.focus();
    }

    /*
     * Which of the panel's two views is on screen. `hidden` rather than a
     * class so the inactive view leaves the accessibility tree and the focus
     * trap above (which skips anything under [hidden]) agrees with it.
     */
    showView(name) {
      this.setAttribute('data-view', name);

      this.querySelectorAll('[data-drawer-view]').forEach(function (view) {
        view.hidden = view.getAttribute('data-drawer-view') !== name;
      });
    }

    /*
     * The tables belong to the product currently in the drawer, so they come
     * with the fetched body and are copied — not moved — into the panel's
     * own view: the next setBody() wipes the body, and a moved node would be
     * orphaned markup the shopper could still be looking at.
     */
    showSizeGuide(opener) {
      var source = this.querySelector('[data-bundle-size-guide]');
      var target = this.querySelector('[data-drawer-size-guide-body]');
      if (!source || !target) return;

      target.innerHTML = source.innerHTML;
      this._sizeGuideReturnFocusTo = opener instanceof HTMLElement ? opener : null;
      this.showView('size-guide');

      /* Scrolled halfway down the product view, the panel would otherwise
         open the chart mid-table. */
      var panel = this.querySelector('.bundle-drawer__panel');
      if (panel) panel.scrollTop = 0;

      var closeButton = this.querySelector('[data-drawer-size-guide-close]');
      if (closeButton) closeButton.focus();
    }

    hideSizeGuide() {
      this.showView('product');

      if (this._sizeGuideReturnFocusTo && document.contains(this._sizeGuideReturnFocusTo)) {
        this._sizeGuideReturnFocusTo.focus();
      }
      this._sizeGuideReturnFocusTo = null;
    }

    /*
     * Size chart ⇄ body measurements. The link names the view it goes TO,
     * so its own label swaps with the tables (Figma 8196:107976 shows it on
     * the body-measurement side, reading "Size conversion").
     */
    toggleSizeGuideView(toggle) {
      /*
       * Scoped to the block the link itself sits in, not to the drawer: the
       * drawer holds the tables TWICE while the size guide is open — the
       * hidden original that came with the fetched body, and the copy on
       * screen. A drawer-wide lookup finds the original first and toggles
       * markup nobody can see.
       */
      var root = toggle.closest('.bundle-size-guide');
      if (!root) return;

      var body = root.querySelector('[data-size-guide-view="body"]');
      if (!body) return;

      var showBody = body.hidden;
      body.hidden = !showBody;

      var charts = root.querySelector('[data-size-guide-view="charts"]');
      if (charts) charts.hidden = showBody;

      toggle.textContent = showBody
        ? toggle.getAttribute('data-label-charts')
        : toggle.getAttribute('data-label-body');
    }

    close() {
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('bundle-drawer-open');
      this._sizeGuideReturnFocusTo = null;

      if (this._returnFocusTo && document.contains(this._returnFocusTo)) {
        this._returnFocusTo.focus();
      }
      this._returnFocusTo = null;
    }

    /*
     * `step`, when given, is stamped onto the freshly-inserted
     * [data-bundle-product] as its data-step — Liquid cannot supply this
     * itself (Shopify's `request` object has no query-string accessor, so
     * sections/bundle-product-drawer.liquid renders data-step="" and relies
     * entirely on this call). The caller (bundle-builder.js) already knows
     * the step from the card/drawer it is acting on, so this is the single
     * place that value actually lands in the DOM, for both the initial
     * open and a colour-swatch swap.
     */
    setBody(html, step) {
      const body = this.querySelector('[data-drawer-body]');
      if (!body) return;
      body.innerHTML = html;

      /* A different product means different charts, so the copy sitting in
         the size-guide view is stale the moment the body changes — back to
         the product view, where the only door into it is. */
      this.showView('product');

      if (step == null) return;
      const product = body.querySelector('[data-bundle-product]');
      if (product) product.setAttribute('data-step', step);
    }
  }

  customElements.define('bundle-drawer', BundleDrawer);
})();
